import { z } from 'zod/v4';

/**
 * Path params of `GET /api/questionnaires/data-points/:dataPointId/companies/:companyId`.
 *
 * `dataPointId` is a datapoint **slug** (`official_name`, `vat_id`), not a database
 * id — the service resolves it through `data-points-lib` to the `retracedId` that
 * actually keys the profile blob. The legacy route declared both params as bare
 * `{type: 'string'}` with no `required` array and no length bound; the lengths here
 * are the column widths the ids are compared against, so an over-long value is a
 * 400 rather than a guaranteed-empty query.
 */
export const CompanyDataPointAnswerParamsSchema = z.object({
  dataPointId: z.string().min(1).max(255),
  companyId: z.string().min(1).max(64),
});

export type CompanyDataPointAnswerParams = z.infer<
  typeof CompanyDataPointAnswerParamsSchema
>;

/**
 * One company-profile value for one datapoint.
 *
 * **Narrower than legacy, deliberately.** The legacy handler sent the raw profile
 * row un-enveloped, which meant two different shapes off one endpoint: a hit
 * carried `forCompanyId` / `byCompanyId` / `onBehalfOfCompanyId` / `profileType`
 * alongside `value`, and a miss carried only `{value: null, dataPointId}` — its
 * integration test pinned the difference with
 * `expect(body.forCompanyId).toBeUndefined()`. No consumer ever read those four
 * fields: both frontends read `value`, and the old frontend also reads
 * `dataPointId` to match the answer back to its question. Dropping them makes the
 * response one shape.
 *
 * `value` is `z.unknown()` because a profile value is genuinely free-form — the
 * stored blob is anything from `{value: 'Acme GmbH'}` to
 * `{value: 42, unit: 'kg', year: 2024, verificationMeta: {...}}` to a bare scalar
 * or an array, depending on the datapoint's answer type. `z.unknown()` renders as
 * the empty JSON schema, so fast-json-stringify passes the blob through verbatim
 * and the key stays required. A declared union would serialize identically but
 * trips four fast-json-stringify strict-mode warnings on its `record` branch, and
 * would silently strip anything the union failed to describe — this is the one
 * field where the wire must stay open.
 *
 * `null` means "no profile row carries this key", which every consumer already
 * treats as "nothing to pre-fill".
 */
export const CompanyDataPointAnswerSchema = z.object({
  /** Echo of the requested slug, so a fanned-out caller can match responses to questions. */
  dataPointId: z.string(),
  value: z.unknown(),
});

export type CompanyDataPointAnswer = z.infer<
  typeof CompanyDataPointAnswerSchema
>;
